import React from 'react';
import { MK3_COLORS } from './mk3/styles';

// Shared internals for the "beam sweeps over a document, extracted values pop
// in as chips" reveal — used by BOTH SampleScanPlayback (the fixed onboarding
// demo doc) and RealScanReveal (the real scan result). Extracted once the
// RealScanReveal design was locked (Daniel approved it in the dev gallery) so
// the two timelines can never visually drift apart from independent edits.
// Pure deduplication: every place the two timelines always differed (sweep
// height/duration/start offset, clip radius, the keyframes name itself,
// found-vs-empty chip content) stays a prop/call-site concern — nothing here
// changes either consumer's visible behavior or timing.

// The beam sweep's own clipping layer — absolutely positioned over whatever
// it's laid on top of (SamplePaper's letter, or RealScanReveal's image/
// generic-glyph placeholder), overflow-hidden to whatever border radius that
// content uses. A sibling of the content rather than a wrapper with
// overflow:hidden itself, so the *outer* box can keep overflow:visible for
// its own drop shadow (SamplePaper additionally needs overflow:visible for
// the FOUND pill, which is allowed to hang off its right edge).
export interface BeamSweepProps {
  sweeping: boolean;
  /** Must match whatever border-radius the content underneath uses. */
  clipRadius: number;
  /** Sweep bar height, as a % of the clipped box's own height. */
  heightPercent: number;
  /** Starting `top`, as a %  — negative, so the bar starts just above the box. */
  topStartPercent: number;
  /** Total sweep duration, `topStartPercent` -> 104%. When `loop` is true, this is ONE direction's duration — the full down+up cycle takes 2x. */
  durationMs: number;
  /**
   * Unique per distinct (heightPercent/topStartPercent) combo — the gallery
   * can mount many instances of both consumers at once, and two different
   * keyframes bodies sharing one @keyframes name would have the last one
   * parsed silently win for all of them.
   */
  keyframesName: string;
  /**
   * When true, sweeps down then back up, repeating indefinitely (a scanner
   * idling) instead of playing once and stopping at 104% — used by
   * RealScanReveal's in-flight "scanning" state. Only one of {loop, one-shot}
   * is ever rendered for a given `keyframesName` at a time (callers toggle
   * this prop rather than mounting two BeamSweeps), so there's no risk of two
   * differently-bodied @keyframes rules sharing the name.
   */
  loop?: boolean;
  /** Extra content clipped to the same layer (e.g. SamplePaper's top accent band). */
  children?: React.ReactNode;
}

export const BeamSweep: React.FC<BeamSweepProps> = ({
  sweeping, clipRadius, heightPercent, topStartPercent, durationMs, keyframesName, loop = false, children,
}) => (
  <div
    aria-hidden
    style={{
      position: 'absolute', inset: 0, borderRadius: clipRadius, overflow: 'hidden', pointerEvents: 'none',
    }}
  >
    {children}
    {sweeping && (
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          height: `${heightPercent}%`,
          background: 'linear-gradient(180deg, rgba(171,114,237,0), rgba(171,114,237,.42), rgba(171,114,237,0))',
          borderTop: '2px solid rgba(171,114,237,.9)',
          boxShadow: '0 0 22px 6px rgba(171,114,237,.5)',
          animation: loop
            ? `${keyframesName} ${durationMs * 2}ms ease-in-out infinite`
            : `${keyframesName} ${durationMs}ms ease-in-out 1 forwards`,
        }}
      />
    )}
    <style>
      {loop
        ? `@keyframes ${keyframesName}{0%{top:${topStartPercent}%}50%{top:104%}100%{top:${topStartPercent}%}}`
        : `@keyframes ${keyframesName}{0%{top:${topStartPercent}%}100%{top:104%}}`}
    </style>
  </div>
);

// Matches the chip label's existing purple ("NAME"/"CATEGORY"/"RENEWS") so
// the non-category chip icons read as one consistent tint rather than
// multicolor emoji. The category icon is left untinted — it's the app's real
// category glyph, which already carries its own brand colour.
export const CHIP_ICON_TINT = '#895BBE';

export const CHIP_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  background: '#fff',
  border: '1px solid #CDAAF4',
  borderRadius: 100,
  padding: '9px 15px',
  fontFamily: 'Grift, sans-serif',
  fontSize: 14,
  fontWeight: 700,
  color: MK3_COLORS.heading,
  boxShadow: '2px 2px 5px rgba(24,0,100,0.12)',
  transition: 'all .45s cubic-bezier(.2,.9,.3,1)',
};

// Icon glyph size for every chip — kept as one constant so ExtractionChip's
// own icon-slot sizing and each consumer's heroicon className stay in sync.
export const CHIP_ICON_SIZE = 22;

// Vertical stack both timelines render their chips into, staggered in one at
// a time as each one's `visible` flips true. Mobile-widened (Daniel: at
// 250px fixed the whole reveal read as "far away" on a phone-width viewport)
// — scales with the viewport up to a desktop-sized cap instead of a single
// fixed width.
export const ChipList: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-col gap-2 mt-4" style={{ width: 'min(86vw, 340px)' }}>
    {children}
  </div>
);

export interface ExtractionChipProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  /** Fades + slides in from `translateY(-18px) scale(.85)` when this flips true. */
  visible: boolean;
  dataCy?: string;
}

export const ExtractionChip: React.FC<ExtractionChipProps> = ({
  icon, label, value, visible, dataCy,
}) => (
  <div
    style={{
      ...CHIP_STYLE,
      opacity: visible ? 1 : 0,
      transform: visible ? 'none' : 'translateY(-18px) scale(.85)',
    }}
    data-cy={dataCy}
  >
    <span
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', width: CHIP_ICON_SIZE, height: CHIP_ICON_SIZE, flexShrink: 0,
      }}
    >
      {icon}
    </span>
    <span
      style={{
        fontWeight: 600, color: '#895BBE', fontSize: 12, letterSpacing: '.06em',
      }}
    >
      {label}
    </span>
    <span>{value}</span>
  </div>
);
